import React, { useState } from 'react';

import { Alignment } from '../../../models/LessonComponentSettings';
import AssetLibrary from '../../../../shared/components/AssetLibrary/AssetLibrary';
import AssetService from '../../../../shared/services/AssetService';
import ChangeFile from '../../../../shared/components/ChangeFile';
import CloseIcon from '@mui/icons-material/Close';
import ColorPickerButton from '../../../../shared/components/ColorPickerButton';
import ColorPickerService from '../../../../shared/services/ColorPickerService';
import { ColumnAsset } from '../../../models/LessonComponentContent';
import DefaultImage from '../../../images/default-image.jpg';
import Grid from '@mui/material/Unstable_Grid2';
import ImageIcon from '@mui/icons-material/Image';
import { InlineQuillEdit } from '../../../components/InlineQuillEdit';
import ParagraphIcon from '../../../images/paragraph-icon.svg';
import { RgbaColor } from '../../../models/LessonComponentStyle';
import Tooltip from '@mui/material/Tooltip';
import styles from '../../../styles/Column.module.css';
import { useTheme } from '@mui/material/styles';

type columnType = 'TEXT' | 'IMAGE';

interface ColumnItemEditorProps {
  column: ColumnAsset;
  componentKey: number;
  position: Alignment;
  handleText: (value: string) => void;
  handleAssetSelect: (assetkey: string) => void;
  handleBackgroundColor: (color: RgbaColor) => void;
}

export default function ColumnItemEditor(props: ColumnItemEditorProps) {
  const muiTheme = useTheme();
  const fontFamily = muiTheme.typography.fontFamily;
  const [type, setType] = useState<columnType>(
    props.column?.assetkey ? 'IMAGE' : props.column?.text ? 'TEXT' : null
  );
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);

  const handleShowAssetLibrary = () => {
    setShowAssetLibrary(true);
    setType('IMAGE');
  };

  const handleCloseAssetLibrary = () => {
    setShowAssetLibrary(false);
  };

  return (
    <div style={{ fontFamily }}>
      <div
        style={{
          padding: type === 'TEXT' ? '30px' : '0px',
          alignItems: !type ? 'center' : props.position,
          justifyContent: 'center',
          backgroundColor: props.column?.backgroundColor
            ? ColorPickerService.rgbToHex(props.column.backgroundColor)
            : undefined
        }}
        className={`${styles.columnContainer} ${
          props.column?.assetkey ? styles.imageColumn : styles.textColumn
        }`}
      >
        {type && type !== 'TEXT' && (
          <>
            <img
              className={styles.image}
              src={
                AssetService.getAssetUrl(props.column.assetkey) || DefaultImage
              }
            />
            <ChangeFile
              assetkey={props.column.assetkey}
              assetTypes={['image']}
              handleSelect={props.handleAssetSelect}
            />
          </>
        )}
        {type && (
          <CloseIcon
            className={styles.closeIcon}
            onClick={() => {
              type === 'TEXT'
                ? props.handleText(null)
                : props.handleAssetSelect(null);
              setType(null);
            }}
            fontSize="small"
          />
        )}
        {!type && (
          <div style={{ cursor: 'pointer' }}>
            <Tooltip title="Select text" placement="bottom-start">
              <img
                src={ParagraphIcon}
                alt="Paragraph"
                onClick={() => {
                  setType('TEXT');
                }}
              />
            </Tooltip>
            <Tooltip title="Select image" placement="bottom-start">
              <ImageIcon
                onClick={handleShowAssetLibrary}
                className={styles.imageIcon}
              />
            </Tooltip>
          </div>
        )}
        {type === 'TEXT' && (
          <Grid
            className={`style-p ${styles.textContainer} column-item-position ${props.position}`}
          >
            <InlineQuillEdit
              placeholder={'Click to add text'}
              value={props.column?.text || ''}
              componentKey={props.componentKey}
              styleType="noList"
              onChange={props.handleText}
            />
          </Grid>
        )}
        {type === 'IMAGE' && showAssetLibrary && (
          <AssetLibrary
            types={['image']}
            category="author"
            title="Select Image"
            selectedAssetkey={props.column.assetkey}
            onSelect={props.handleAssetSelect}
            onClose={handleCloseAssetLibrary}
          />
        )}
      </div>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid className={styles.colorGrid}>Background color</Grid>
        <Grid>
          <ColorPickerButton
            color={props.column.backgroundColor}
            resetButton
            isRgba
            onChange={props.handleBackgroundColor}
          />
        </Grid>
      </Grid>
    </div>
  );
}
